import React, { useEffect } from "react";
import { X } from "lucide-react";
import { FilterState } from "../../types";
import { FilterPanel } from "./FilterPanel";

type Props = {
  open: boolean;
  onClose: () => void;
  filters: FilterState;
  onFilterChange: (filters: Partial<FilterState>) => void;
  onClear: () => void;
  onApply: () => void;
};

export const MobileFiltersSheet: React.FC<Props> = ({
  open,
  onClose,
  filters,
  onFilterChange,
  onClear,
  onApply,
}) => {
  // Lock scroll behind the sheet
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] lg:hidden">
      {/* Backdrop */}
      <button
        aria-label="Close filters"
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]"
      />

      {/* Bottom sheet */}
      <div className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-[520px]">
        <div className="rounded-t-3xl bg-white shadow-2xl border border-slate-100 overflow-hidden">
          {/* Header (sticky) */}
          <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-md border-b border-slate-100">
            <div className="h-14 px-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-1.5 w-10 rounded-full bg-slate-200 mx-auto absolute left-1/2 -translate-x-1/2 top-2" />
                <h2 className="text-base font-bold text-slate-900">Filters</h2>
              </div>
              <button
                onClick={onClose}
                className="h-10 w-10 rounded-full bg-slate-50 hover:bg-slate-100 flex items-center justify-center text-slate-600"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div
            className="max-h-[72vh] overflow-y-auto px-4 pt-4 pb-[calc(env(safe-area-inset-bottom)+96px)]"
          >
            {/* We reuse existing FilterPanel but force it into "modal/sheet" mode via wrapper styles */}
            <FilterPanel
              filters={filters}
              onFilterChange={onFilterChange}
              onClose={onClose}
              onClear={onClear}
              hideFooter
            />
          </div>

          {/* Footer (sticky) */}
          <div className="sticky bottom-0 z-10 bg-white/95 backdrop-blur-md border-t border-slate-100 px-5 py-4 pb-[calc(env(safe-area-inset-bottom)+16px)]">
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={onClear}
                className="h-12 rounded-xl border border-slate-200 bg-white text-slate-700 font-semibold text-sm active:scale-[0.99]"
              >
                Clear all
              </button>
              <button
                onClick={() => {
                  onApply();
                  onClose();
                }}
                className="h-12 rounded-xl bg-slate-900 text-white font-semibold text-sm shadow-md active:scale-[0.99]"
              >
                Show results
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
